import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Output, signal } from '@angular/core';
import { SettingsService } from '@app/_services/settings.service';
import { ReleaseConfigService } from '@app/_services/release-config.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

type SupportedPlatform = 'android' | 'ios' | 'windows' | 'macos';

const STORE_KEY_DISMISSED = 'appBannerDismissed';
const STORE_KEY_DATE = 'appBannerDate';

const DOWNLOAD_URLS: Record<SupportedPlatform, string> = {
    android: `https://play.google.com/store/apps/details?id=${BRAND_CONFIG.bundleId}`,
    ios: `https://apps.apple.com/app/${BRAND_CONFIG.id}/id${BRAND_CONFIG.appStoreIds.prod}`,
    windows: `${BRAND_CONFIG.marketingUrl}/download/windows`,
    macos: `${BRAND_CONFIG.marketingUrl}/download/macos`,
};

@Component({
    selector: 'app-download-banner',
    imports: [CommonModule, SvgComponent],
    templateUrl: './app-download-banner.component.html',
    styleUrl: './app-download-banner.component.scss'
})
export class AppDownloadBannerComponent {

    @Output() dismissed$ = new EventEmitter<void>();
    private dismissed = signal(false);
    public ss = inject(SettingsService);
    private releaseConfigService = inject(ReleaseConfigService);

    readonly brandName = BRAND_CONFIG.name;

    platform: SupportedPlatform | null = this.detectPlatform();

    showBanner = computed(() => {
        if (!this.platform) return false;
        if (this.dismissed()) return false;
        if (this.wasDismissedToday()) return false;
        // Hide download banner when update banner is visible
        if (this.releaseConfigService.versionStatus() === 'deprecated'
            && !this.releaseConfigService.isDismissed()) return false;
        return true;
    });

    constructor() {}

    get storeIcon(): string {
        if (this.platform === 'ios' || this.platform === 'macos') return 'apple';
        if (this.platform === 'windows') return 'windows';
        return 'androidPlaystore'; // android fallback
    }

    onGetApp(): void {
        if (!this.platform) return;
        window.open(BRAND_CONFIG.marketingUrl, '_blank');
    }

    onDismiss(): void {
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem(STORE_KEY_DISMISSED, 'true');
        localStorage.setItem(STORE_KEY_DATE, today);
        this.dismissed.set(true);
        this.dismissed$.emit();
    }

    private wasDismissedToday(): boolean {
        const storedDate = localStorage.getItem(STORE_KEY_DATE);
        if (!storedDate) return false;
        const today = new Date().toISOString().slice(0, 10);
        return storedDate === today;
    }

    private detectPlatform(): SupportedPlatform | null {
        const ua = navigator.userAgent;
        if (/android/i.test(ua)) return 'android';
        if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
        if (/windows nt/i.test(ua)) return 'windows';
        if (/macintosh|mac os x/i.test(ua) && !/chrome/i.test(ua)) return 'macos';
        return null;
    }
}
